import React, { FC, useContext, useState } from 'react';
import { Box, CircularProgress, Typography } from '@material-ui/core';
import EditUserEntities from './EditUserEntities/EditUserEntities';
import { useApolloClient, useMutation, useQuery } from '@apollo/react-hooks';
import {
  GetUserInfos,
  GetUserInfosVariables,
} from '../../../graphql/user/types/GetUserInfos';
import { GET_USER_INFOS } from '../../../graphql/user/query';
import moment from 'moment';
import UserDetailHeader from './Header/UserDetailHeader';
import UserCoordonnees from './UserCoordonnees';
import EntityManagingList from '../ManageEntities/List/EntityManagingList';
import UserRattachement from './UserEnseigneAndEntities/UserRattachement';
import UserRegimeAndId from './RegimeAndIdentifiants/UserRegimeAndId';
import UserProfil from './Profil/UserProfil';
import { IUserContext, UserContext } from '../../../providers/UserProvider';
import { GraphqlError, Operation, Resource } from '../../../common/constants';
import {
  AdminEditUser,
  AdminEditUserVariables,
} from '../../../graphql/user/types/AdminEditUser';
import { ADMIN_EDIT_USER } from '../../../graphql/user/mutation';
import { displaySnackBar } from '../../../common/displaySnackbar';
import {
  ICommission,
  initialUserState,
  IUserEditState,
  mapUserDataToState,
} from './utils';
import ConfirmationButtons from '../../ConfirmationButtons/ConfirmationButtons';
import { DistributeCommissions } from './DistributeCommissions/DistributeCommissions';
import { EditUserCommissionModal } from './EditUserCommission/EditUserCommissionModal';
import ConfirmationModal from '../../../common/components/ConfirmationModal';
import CustomDialog from '../../../common/components/CustomDialog';

interface Params {
  userId: number;
  setUserId(id: number | null): void;
  handleClose?: () => void;
}

const UserAdditionalInfosModal: FC<Params> = (props) => {
  const { userId, setUserId, handleClose: _handleClose } = props;
  const [userState, setUserState] = useState<IUserEditState>(initialUserState);
  const { canI } = useContext<IUserContext>(UserContext);
  const [isEditingEntities, setIsEditingEntities] = useState(false);
  const [isEditingCommission, setIsEditingCommission] = useState(false);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const [selectedCommission, setSelectedCommission] = useState<ICommission>();
  const [mandataryFullName, setMandataryFullName] = useState<string>();

  const client = useApolloClient();

  const handleClose = () => {
    _handleClose && _handleClose();
    setUserId(null);
  };

  const [editUser, { loading: loadingEditUser }] = useMutation<
    AdminEditUser,
    AdminEditUserVariables
  >(ADMIN_EDIT_USER, {
    onCompleted: () => {
      displaySnackBar(client, {
        type: 'SUCCESS',
        message: 'Utilisateur modifié avec succès',
        isOpen: true,
      });
    },
    onError: (error) => {
      if (error.graphQLErrors.length) {
        if (error.graphQLErrors[0].extensions) {
          const code = error.graphQLErrors[0].extensions['original']['name'];
          if (code === GraphqlError.CRM_ID_EXIST) {
            displaySnackBar(client, {
              type: 'ERROR',
              message: 'idCRM existant',
              isOpen: true,
            });
          }
        }
      }
    },
  });

  const { loading: loadingQuery } = useQuery<
    GetUserInfos,
    GetUserInfosVariables
  >(GET_USER_INFOS, {
    variables: { id: userId },
    onCompleted: (data) => {
      if (data && data.getUser) {
        setUserState(mapUserDataToState(data.getUser));
        if (data?.getUser?.mandatary)
          setMandataryFullName(
            `${data?.getUser?.mandatary?.firstName} ${data?.getUser?.mandatary?.lastName}`,
          );
      }
    },
  });

  const totalBeneficiaryCommissions = (
    userState?.defaultCommissionDistributions || []
  ).reduce((acc, currentValue) => {
    /** Ne pas prendre en compte le montant du commission actuel si on fait une modification */
    if (selectedCommission && isEditingCommission) {
      if (selectedCommission?.id !== currentValue?.id)
        return currentValue?.amount ? currentValue?.amount + acc : acc;
      return acc;
    }
    return currentValue?.amount ? currentValue?.amount + acc : acc;
  }, 0);

  const totalCommissionMax =
    userState.totalDefaultCommission - totalBeneficiaryCommissions;

  if (loadingQuery)
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={100}>
        <CircularProgress />
      </Box>
    );

  // Header
  const { avatarUrl, roles } = userState;
  const fullName = `${userState.firstName} ${
    userState.lastName ? userState.lastName : ''
  }`;
  const createdAt = userState.createdAt
    ? `${moment(userState.createdAt).format('DD-MM-YYYY')} à ${moment(
        userState.createdAt,
      ).format('HH:mm')}`
    : '';

  // Rattachement
  const enseigneName = userState.userEnseigne
    ? userState.userEnseigne.name
    : '';
  const entitiesName = userState.entities
    ? userState.entities.map((entity) => entity.name)
    : [];

  const canEditUser = canI(Operation.UPDATE, Resource.USERS);

  const handleSave = () => {
    const {
      entities,
      userEnseigne,
      createdAt,
      avatarUrl,
      defaultCommissionDistributions,
      totalDefaultCommission,
      idCRM,
      ...mutationVariables
    } = userState;

    const entityIds = userState.entities
      ? userState.entities.map((entity) => entity.id)
      : undefined;

    const enseigneId = userState.userEnseigne?.id;

    const managedEntityIds =
      userEnseigne && userEnseigne.entities
        ? userEnseigne.entities
            .filter((entity) => entity.managed)
            .map((entity) => entity.id)
        : [];

    const defaultCommissionDistributionsInput = userState?.defaultCommissionDistributions?.map(
      (commission) => ({
        commissionId: !commission.isNew ? commission?.id : null,
        amount: commission?.amount || 0,
        beneficiaryId: commission?.beneficiary?.id || 0,
      }),
    );

    editUser({
      variables: {
        input: {
          enseigneId,
          entityIds,
          managedEntityIds,
          idCRM: idCRM ? +idCRM : null,
          defaultCommissionDistributions: defaultCommissionDistributionsInput,
          totalDefaultCommission: Number.parseInt(
            userState?.totalDefaultCommission as any,
          ),
          ...mutationVariables,
        },
      },
    }).finally(() => {
      handleClose();
    });
  };

  const handleEditCommissions = (commissionInfo: ICommission) => {
    console.log(
      'defaultcommission number : ',
      userState?.defaultCommissionDistributions?.length,
    );
    console.log('commissionInfo', commissionInfo);
    const newCommission: ICommission = {
      id:
        commissionInfo?.id === 0
          ? userState?.defaultCommissionDistributions
            ? userState?.defaultCommissionDistributions.length + 1000
            : 1
          : commissionInfo?.id,
      amount: commissionInfo?.amount,
      beneficiary: {
        id: commissionInfo?.beneficiary?.id || 0,
        firstName: commissionInfo?.beneficiary?.firstName || '',
        lastName: commissionInfo?.beneficiary?.lastName || '',
      },
      isNew: commissionInfo?.isNew,
    };

    console.log('new commission to add: ', newCommission);

    // ================== Update the commissions ==================

    setUserState((prevState) => {
      if (
        prevState?.defaultCommissionDistributions &&
        prevState?.defaultCommissionDistributions?.length > 0
      ) {
        const commission = prevState?.defaultCommissionDistributions?.find(
          (commission) => commission?.id === newCommission?.id,
        );

        // ================== Edit an existing commission ==================

        if (commission !== undefined) {
          return {
            ...prevState,
            defaultCommissionDistributions: prevState?.defaultCommissionDistributions?.map(
              (_commission) => {
                if (_commission?.id === commission?.id)
                  return {
                    ..._commission,
                    beneficiary: newCommission?.beneficiary,
                    amount: newCommission?.amount,
                  };
                return _commission;
              },
            ),
          };
        }

        //  ================== Add new commission ==================

        return {
          ...prevState,
          defaultCommissionDistributions: [
            ...prevState.defaultCommissionDistributions,
            ...[newCommission],
          ],
        };
      }

      //  ================== Add the first commission ==================

      return {
        ...prevState,
        defaultCommissionDistributions: [newCommission],
      };
    });

    setIsEditingCommission(false);
  };

  const currentBeneficiaryIds = userState.defaultCommissionDistributions
    ? userState.defaultCommissionDistributions.map(
        (commission) => commission.beneficiary?.id,
      )
    : [];

  currentBeneficiaryIds.push(userId); // remove current user in list of beneficiary to add

  const Header = (
    <UserDetailHeader
      avatarUrl={avatarUrl}
      fullName={fullName}
      roles={roles}
      enseigneName={enseigneName}
      createdAt={createdAt}
      mandataryFullName={mandataryFullName}
      userState={userState}
      setUserState={setUserState}
    />
  );

  return (
    <CustomDialog
      loading={false}
      maxWidth={isEditingEntities ? 'sm' : 'md'}
      onClose={handleClose}
      open={true}>
      {Header}
      {isEditingEntities ? (
        <EditUserEntities
          setIsEditingEntities={setIsEditingEntities}
          userState={userState}
          setUserState={setUserState}
        />
      ) : (
        <>
          <UserCoordonnees
            userState={userState}
            setUserState={setUserState}
            canEditUser={canEditUser}
          />
          <UserRattachement
            setIsEditingEntities={setIsEditingEntities}
            enseigneName={enseigneName}
            entitiesName={entitiesName}
            canEditUser={canEditUser}
          />
          <UserProfil
            canEditUser={canEditUser}
            userState={userState}
            setUserState={setUserState}
          />
          <UserRegimeAndId
            userState={userState}
            setUserState={setUserState}
            canEditUser={canEditUser}
          />
          <EntityManagingList
            userId={userId}
            canEditUser={canEditUser}
            userState={userState}
            setUserState={setUserState}
          />
          <DistributeCommissions
            personelFullName={fullName}
            canEditUser={canEditUser}
            onRemoveBeneneficiaries={() => {}}
            setUserState={setUserState}
            totalCommission={userState?.totalDefaultCommission}
            onCommissionDeleteClick={(commissionToDelete) => {
              setSelectedCommission(commissionToDelete);
              setOpenDeleteConfirmation(true);
            }}
            onCommissionEditClick={(selectedCommission) => {
              setSelectedCommission(selectedCommission);
              setIsEditingCommission(true);
            }}
            commissions={userState?.defaultCommissionDistributions || []}
            onAddNewBeneneficiary={() => {
              setIsEditingCommission(true);
              setSelectedCommission(undefined);
            }}
          />
          <ConfirmationButtons
            handleCancel={handleClose}
            handleConfirm={handleSave}
            disableConfirm={loadingEditUser || loadingQuery}
          />
        </>
      )}

      {/* ======================== Comfirmation delete commission dialog ======================= */}
      <ConfirmationModal
        content={
          <Typography style={{ display: 'flex' }}>
            Voulez-vous vraiment supprimer{' '}
            <span style={{ fontWeight: 'bold', marginLeft: 4, marginRight: 4 }}>
              {` ${selectedCommission?.beneficiary?.firstName.toUpperCase()} ${
                selectedCommission?.beneficiary?.lastName
              }`}{' '}
            </span>
            <span>?</span>
          </Typography>
        }
        onCancel={() => {
          setOpenDeleteConfirmation(false);
          setSelectedCommission(undefined);
        }}
        onConfirm={() => {
          setUserState((prevState) => {
            if (
              prevState?.defaultCommissionDistributions &&
              prevState?.defaultCommissionDistributions?.length > 0
            ) {
              const newCommissions = prevState?.defaultCommissionDistributions?.filter(
                (commission) =>
                  commission?.beneficiary?.id !==
                  selectedCommission?.beneficiary?.id,
              );
              return {
                ...prevState,
                defaultCommissionDistributions: newCommissions,
              };
            }
            return prevState;
          });
          setSelectedCommission(undefined);
          setOpenDeleteConfirmation(false);
        }}
        open={openDeleteConfirmation}
        confirmLabel="Oui, supprimer"
      />

      {/* ======================== Edit commission dialog ======================= */}
      <CustomDialog open={isEditingCommission} title={Header}>
        <EditUserCommissionModal
          onCancelEdit={() => setIsEditingCommission(false)}
          isEditingCommission={isEditingCommission}
          commissionInfo={selectedCommission}
          onConfirmEditingCommission={handleEditCommissions}
          totalCommissionMax={totalCommissionMax}
          currentBeneficiaryIds={currentBeneficiaryIds as number[]}
          userId={userState?.userId}
        />
      </CustomDialog>
    </CustomDialog>
  );
};

export default UserAdditionalInfosModal;
